import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export interface CriticalPowerPoint {
  duration: number // in seconds
  power: number // in watts
  label: string // e.g., "5s", "1min", "5min", "20min", "60min"
  isKey: boolean // whether this is a key metric to display in StatCards
  description?: string // for key metrics, provide a description
}

export interface CPModelEstimate {
  cp: number
  wPrime: number
  r2: number
  durations: number[]
}

export interface CPReference {
  dateRange: string[]
  values: CriticalPowerPoint[]
}

export interface WBalPoint {
  time: number
  wBal: number
  power: number
}

interface PowerStats {
  idleTime: number
  average: number
  stdDev: number
  min: number
  max: number
  sampleCount: number
}

interface UsePowerStatsReturn {
  stats: PowerStats | null
  loading: boolean
  error: string | null
  refreshStats: () => Promise<void>
}

export const usePowerStats = (workoutId: string): UsePowerStatsReturn => {
  const [stats, setStats] = useState<PowerStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPowerStats = useCallback(async () => {
    if (!workoutId) return

    try {
      setLoading(true)
      setError(null)

      // Fetch heart rate metrics for the workout
      const { data: metrics, error: metricsError } = await supabase
        .from('workout_metrics')
        .select('values')
        .eq('workout_id', workoutId)
        .eq('metric_name', 'power')
        .single()

      if (metricsError) {
        // If no data found, this is not an error - just no data available
        if (metricsError.code === 'PGRST116') {
          setStats(null)
          setLoading(false)
          return
        }
        throw new Error(metricsError.message)
      }

      if (!metrics || !metrics.values || metrics.values.length === 0) {
        setStats(null)
        setLoading(false)
        return
      }

      // Extract non-null values from the array
      const values = metrics.values.filter((val: number | null) => val !== null) as number[]
      if (values.length === 0) {
        setStats(null)
        setLoading(false)
        return
      }
      const n = values.length

      // Calculate mean
      const mean = values.reduce((sum, hr) => sum + hr, 0) / n

      // Calculate standard deviation
      const squaredDiffs = values.map((hr) => (hr - mean) ** 2)
      const variance = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / n
      const stdDev = Math.sqrt(variance)

      // Calculate min and max
      const min = Math.min(...values)
      const max = Math.max(...values)

      const idleTime = values.reduce((sum, power) => {
        if (power < 25) {
          return sum + 1
        } else {
          return sum
        }
      }, 0)

      setStats({
        idleTime,
        average: mean,
        stdDev: stdDev,
        min: min,
        max: max,
        sampleCount: n,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load heart rate statistics')
      console.error('Error loading heart rate statistics:', err)
    } finally {
      setLoading(false)
    }
  }, [workoutId])

  const refreshStats = useCallback(async () => {
    await loadPowerStats()
  }, [loadPowerStats])

  useEffect(() => {
    if (workoutId) {
      loadPowerStats()
    }
  }, [workoutId, loadPowerStats])

  return {
    stats,
    loading,
    error,
    refreshStats,
  }
}
